"use client";

import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import Text from "@components/atoms/Text";
import assetLoader from "@utils/internalImageLoder";
import { CarCardProps } from "./CarCard.types";
import styles from "./CarCard.module.scss";
import { formatCurrencyINR, formatPrice, maskAmount } from "@utils/numberFormatter";
import { formatDeliveryDate } from "@utils/formatDate";
import { toKebabCase } from "@utils/stringFormatters";
import ImageResponsive from "@components/atoms/ImageResponsive";
import { getCarAvailability } from "@utils/carAvailability";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { normalizeCityName } from "@utils/urlNormalization";

export const CarCard = ({
  modelName,
  brandName,
  cityName,
  savings,
  minPrice,
  modelExpressDeliveryFlag = true,
  waitingPeriod,
  isLoggedIn,
  slug,
  carouselSection,
  imageLoading = "lazy",
  status,
  pageType,
  backgroundIndex = 1,
}: CarCardProps) => {
  const router = useRouter();
  const isDiscontinued = status === "out_of_production";
  const nonSellable = status === "non_sellable";
  const isSellable = !nonSellable && !isDiscontinued;
  const carAvailability = getCarAvailability(status);
  const modelHref = `/cars/${slug}`;
  const priceHref = `/cars/${slug}/price-in-${normalizeCityName(cityName)}/`;

  const trackClick = (linkIntent: "model" | "price") =>
    trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_similar_cars",
        ...(carouselSection && { section: carouselSection }),
        car_availability: carAvailability,
        city_name: cityName,
        vehicle_make: brandName,
        vehicle_model: modelName,
        link_intent: linkIntent,
      },
    });

  const createClickHandler = (linkIntent: "model" | "price") => () => {
    void trackClick(linkIntent);
  };

  const handleImageClick = async () => {
    await trackClick("model");
    router.push(modelHref);
  };

  return (
    <div className={styles.card}>
      <div
        className={`${styles.imageWrapper} ${styles.imageCta}`}
        role="link"
        tabIndex={0}
        aria-label={`View details for ${brandName} ${modelName}`}
        onClick={() => {
          void handleImageClick();
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            void handleImageClick();
          }
        }}
      >
        <Image
          src={`${assetLoader()}/icons/background/SimilarCardBackground${backgroundIndex}.webp`}
          alt="Background"
          fill
          className="object-cover"
          {...(imageLoading === "priority"
            ? { priority: true }
            : { loading: "lazy" })}
        />

        {modelExpressDeliveryFlag && (
          <div className={styles.expressDelivery}>
            <Image
              src={`${assetLoader()}/icons/ExpressDelivery.svg`}
              alt={"Express delivery"}
              className={styles.chevronDown}
              width={16}
              height={16}
              loading="lazy"
            />
            {"Express delivery"}
            {isLoggedIn && waitingPeriod
              ? ` by ${formatDeliveryDate(parseInt(waitingPeriod))}`
              : ""}
          </div>
        )}

        <div className={styles.carImageContainer}>
          <ImageResponsive
            filePath={`${toKebabCase(brandName)}/${toKebabCase(modelName)}`}
            imageTag={`Hero-Transparent`}
            alt={modelName}
            width={300}
            height={120}
            className={styles.carImage}
            {...(imageLoading === "priority"
              ? { priority: true }
              : { loading: "lazy" })}
          />
        </div>
      </div>

      <div className={styles.cardContent}>
        <Link
          href={modelHref}
          prefetch={false}
          onClick={createClickHandler("model")}
          className={styles.modelLink}
        >
          <Text
            className={styles.modelName}
          >{`${brandName} ${modelName}`}</Text>
        </Link>
        <Text className={styles.cityNote}>
          <Link
            href={priceHref}
            prefetch={false}
            onClick={createClickHandler("price")}
            className={styles.cityNoteLink}
          >
            {`On-road price in ${cityName}`}
          </Link>
          {" starts from"}
        </Text>
        <div className={styles.priceWrapper}>
          <Text className={styles.minPrice}>
            {`${formatPrice(minPrice)}`}
          </Text>
          {isSellable && (
            <Text className={styles.savings}>
              {`Get up to ${
                isLoggedIn ? formatCurrencyINR(savings) : maskAmount(savings)
              } off.`}
            </Text>
          )}
          {!isSellable && (
            <div className={styles.discontinuedBadge}>
              {isDiscontinued && (
                <>
                  <Image
                    src={`${assetLoader()}/icons/close_circular_light.svg`}
                    alt="Discontinued"
                    width={12}
                    height={12}
                    className={styles.closeIcon}
                  />
                  <Text className={styles.discontinuedText}>
                    Discontinued
                  </Text>
                </>
              )}
              {nonSellable && (
                <>
                  <Image
                    src={`${assetLoader()}/icons/Carousal/Exclamation.svg`}
                    alt="Unavailable"
                    width={12}
                    height={12}
                    className={styles.closeIcon}
                  />
                  <Text className={styles.discontinuedText}>Unavailable</Text>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
